// VoicePanel — full-screen voice mode ("Scan & Talk") for a public form.
//
// Rendered by FormChatScreen in place of the typed chat
// when the customer picks "Talk it through". All session logic lives in
// useVoiceSession (owned by the screen so start() can run inside the tap
// gesture); this component is presentational.
//
// Layout (no chat transcript — it distracted more than it helped):
//   - Top banner: the form's image (~1/3 of the screen) with the title.
//   - Live answers card that fills in as the customer speaks; it becomes
//     editable inline when the agent opens review, and Submit happens right
//     here (always a human tap, never the model).
//   - "Photos" button opens a swipeable gallery of the form's images
//     mid-call (also used by the agent's show_image, its pick first).

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import { UseVoiceSession, VoiceDraft } from '../hooks/useVoiceSession';
import { isImageTypeHint, isStagedPhotoUrl } from '../utils/imageQuestions';
import { uploadFormImage } from '../services/ImageUploadService';
import PhotoGallery, { LiveBars } from './PhotoGallery';
import BusinessLogo from './BusinessLogo';
import { useBusinessTheme } from '../context/BusinessThemeContext';
import { DISPLAY_FONT, tok } from '../theme/businessTheme';

/** The slice of the target the panel renders. PublicForm satisfies this
    structurally. */
export interface VoicePanelTarget {
  title: string;
  voiceDisclosure?: string;
  formImageUrl?: string;
  imageUrls?: string[];
}

interface VoicePanelProps {
  form: VoicePanelTarget;
  voice: UseVoiceSession;
  onExit: () => void;
  /** After a successful finish, Done lands the customer in the typed chat —
      all follow-up questions happen there. */
  onDoneToChat: () => void;
}

const VoicePanel: React.FC<VoicePanelProps> = ({
  form,
  voice,
  onExit,
  onDoneToChat,
}) => {
  const theme = useTheme();
  const business = useBusinessTheme();
  const {
    status,
    error,
    fields,
    draft,
    reviewDraft,
    submitted,
    keepOpen,
    formMeta,
    linkOffer,
    cameraRequest,
    completePhotoCapture,
    cancelPhotoCapture,
    start,
    stop,
    submitReview,
    dismissReview,
  } = voice;

  const micLive = status === 'connecting' || status === 'live';
  // The finished screen: a form session ends at the human-tap submit —
  // unless the agent can book appointments (keepOpen): then the call stays
  // live so the customer can book right after submitting.
  const finished = submitted && (!keepOpen || status === 'ended');
  const reviewing = reviewDraft !== null && !finished;
  const disclosure =
    formMeta?.voiceDisclosure || form.voiceDisclosure || voice.disclosure;

  // On transfer the agent may move to a linked form — prefer live meta.
  const title = formMeta?.title || form.title;
  const businessName = formMeta?.businessName;
  const heroTitle = business.displayName || businessName || title;
  const imageUrls: string[] =
    (formMeta?.imageUrls && formMeta.imageUrls.length > 0
      ? formMeta.imageUrls
      : form.imageUrls) || [];

  const [showImages, setShowImages] = useState(false);

  // Photo gallery — opened by the Photos button OR by the agent (show_image).
  // In the agent case its image leads the gallery so it's what the customer
  // sees first; the rest are a swipe away.
  const viewerUrls: string[] = voice.shownImage
    ? [voice.shownImage, ...imageUrls.filter((u) => u !== voice.shownImage)]
    : imageUrls;
  const viewerOpen = showImages || voice.shownImage !== null;
  const closeViewer = () => {
    setShowImages(false);
    voice.closeShownImage();
  };

  // Editable copy of the review draft while review is open.
  const [edits, setEdits] = useState<VoiceDraft>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  useEffect(() => {
    if (reviewDraft) {
      setEdits({ ...reviewDraft });
      setSubmitError(null);
    }
  }, [reviewDraft]);

  // ===== request_photo camera sheet (spec §5) =====
  // The agent's request_photo tool call opened cameraRequest; the mic is
  // already muted (useVoiceSession). completePhotoCapture uploads + POSTs
  // /api/voice/photo and only then releases the deferred function_call_output.
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const cameraFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cameraRequest) {
      setPhotoError(null);
      // Open the native picker as soon as the sheet appears.
      cameraFileInputRef.current?.click();
    }
  }, [cameraRequest]);

  const handleCameraFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoUploading(true);
    setPhotoError(null);
    const outcome = await completePhotoCapture(file);
    setPhotoUploading(false);
    if (!outcome.ok && outcome.error) setPhotoError(outcome.error);
  };

  // ===== Review screen: retake for image-typed fields =====
  const imageFieldNames = new Set(fields.filter((f) => isImageTypeHint(f.type)).map((f) => f.name));
  const [retakingField, setRetakingField] = useState<string | null>(null);
  const [retakeError, setRetakeError] = useState<Record<string, string>>({});
  const retakeFileInputRef = useRef<HTMLInputElement>(null);
  const retakeFieldRef = useRef<string | null>(null);

  const triggerRetake = (fieldName: string) => {
    if (retakingField) return;
    retakeFieldRef.current = fieldName;
    retakeFileInputRef.current?.click();
  };

  const handleRetakeFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const fieldName = retakeFieldRef.current;
    if (!file || !fieldName) return;
    setRetakingField(fieldName);
    setRetakeError((prev) => ({ ...prev, [fieldName]: '' }));
    try {
      const url = await uploadFormImage({
        file,
        channelUid: formMeta?.channelUid || '',
        formId: formMeta?.id || '',
      });
      setEdits((prev) => ({ ...prev, [fieldName]: url }));
    } catch (err: any) {
      setRetakeError((prev) => ({ ...prev, [fieldName]: 'Photo upload failed — try again.' }));
    } finally {
      setRetakingField(null);
      retakeFieldRef.current = null;
    }
  };

  const handleSubmit = async () => {
    // A required image field with no staged photo blocks submit, same as
    // the typed-chat review sheet (spec §5.6).
    const missingRequiredPhoto = fields.find(
      (f) => imageFieldNames.has(f.name) && !f.optional && !isStagedPhotoUrl(edits[f.name]),
    );
    if (missingRequiredPhoto) {
      setSubmitError(`"${missingRequiredPhoto.name}" still needs a photo.`);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    const outcome = await submitReview(edits);
    setSubmitting(false);
    // 422 hands control back to the agent; only surface errors while
    // review is still open.
    if (!outcome.ok && outcome.error) setSubmitError(outcome.error);
  };

  const statusLabel = cameraRequest
    ? 'Mic paused 📷'
    : status === 'connecting'
      ? 'Connecting…'
      : status === 'live'
        ? reviewing
          ? 'Check your answers below'
          : submitted && keepOpen
            ? (formMeta?.canBook ? 'Sent — ask to book a time' : 'Sent — anything else?')
            : 'Live — just talk'
        : status === 'ended'
          ? 'Call ended'
          : 'Ready when you are';

  const primaryButtonSx = {
    borderRadius: 999,
    py: 1.5,
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: '1rem',
    color: tok('onAction'),
    background: tok('action'),
    boxShadow: 'none',
    '&:hover': { background: tok('action'), boxShadow: 'none' },
  };

  // Live status pill, shared by both banner layouts.
  const statusChip = (
    <Box
      role="status"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.1,
        py: 0.3,
        borderRadius: 999,
        backgroundColor: tok('secondaryAction'),
        color: tok('onSecondaryAction'),
      }}
    >
      {status === 'live' ? (
        <LiveBars color={tok('accent')} />
      ) : micLive ? (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: tok('accent'),
            animation: 'voicePulse 1.2s ease-in-out infinite',
            '@keyframes voicePulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.35 },
            },
          }}
        />
      ) : null}
      <Typography sx={{ color: 'inherit', fontWeight: 500, fontSize: 13 }}>
        {statusLabel}
      </Typography>
    </Box>
  );

  const pillBtnSx = {
    borderRadius: 999,
    px: 1.75,
    textTransform: 'none' as const,
    fontWeight: 600,
    background: tok('secondaryAction'),
    color: tok('onSecondaryAction'),
    '&:hover': { background: tok('secondaryAction') },
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        backgroundColor: tok('background'),
        color: tok('onBackground'),
      }}
    >
      {/* ===== Compact branded header (board C): back, logo in a live ring,
          business name + status. Kept to one row so the answers get the
          screen. ===== */}
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          px: 1.5,
          pt: 'max(10px, env(safe-area-inset-top))',
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <IconButton
          onClick={onExit}
          aria-label="Exit voice mode"
          sx={{ width: 40, height: 40, flexShrink: 0, background: tok('secondaryAction'), color: tok('onSecondaryAction'), '&:hover': { background: tok('secondaryAction') } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          {status === 'live' && (
            <Box
              sx={{
                position: 'absolute',
                inset: -6,
                borderRadius: '50%',
                border: `2px solid ${tok('accent')}`,
                animation: 'voiceRing 1.8s ease-out infinite',
                '@keyframes voiceRing': {
                  '0%': { transform: 'scale(0.92)', opacity: 0.9 },
                  '100%': { transform: 'scale(1.25)', opacity: 0 },
                },
              }}
            />
          )}
          <BusinessLogo name={heroTitle} logoUrl={business.logoUrl} size={48} radius="50%" ring={tok('accent')} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h1"
            noWrap
            sx={{ fontFamily: DISPLAY_FONT, fontWeight: 800, fontSize: 19, lineHeight: 1.2, letterSpacing: '-0.02em', color: tok('onBackground') }}
          >
            {heroTitle}
          </Typography>
          <Box sx={{ mt: 0.5 }}>{statusChip}</Box>
        </Box>
        {imageUrls.length > 0 && (
          <IconButton
            onClick={() => setShowImages(true)}
            aria-label={`View photos (${imageUrls.length})`}
            sx={{ width: 40, height: 40, flexShrink: 0, background: tok('secondaryAction'), color: tok('onSecondaryAction'), '&:hover': { background: tok('secondaryAction') } }}
          >
            <PhotoLibraryIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      {/* ===== Consent caption ===== */}
      {micLive && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            px: 2,
            py: 0.75,
            backgroundColor: tok('dock'),
          }}
        >
          <MicIcon sx={{ fontSize: 14, color: 'error.main' }} />
          <Typography variant="caption" sx={{ textAlign: 'center', color: tok('onBackgroundMuted') }}>
            {disclosure}
          </Typography>
        </Box>
      )}

      {/* ===== Main area ===== */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, minHeight: 0 }}>
        {status === 'error' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              gap: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: tok('onBackground') }}>Couldn't start voice</Typography>
            <Typography variant="body2" sx={{ color: tok('onBackgroundMuted') }}>
              {error || 'Something went wrong.'}
            </Typography>
            <Button variant="contained" startIcon={<MicIcon />} onClick={start} sx={primaryButtonSx}>
              Try again
            </Button>
            <Button color="inherit" onClick={onExit} sx={{ textTransform: 'none', color: tok('onBackground') }}>
              Back
            </Button>
          </Box>
        ) : finished ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              gap: 1.5,
              animation: 'voiceRise 0.45s cubic-bezier(0.22, 1, 0.36, 1)',
              '@keyframes voiceRise': {
                from: { opacity: 0, transform: 'translateY(14px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 92,
                height: 92,
                borderRadius: '50%',
                background: tok('secondaryAction'),
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 56, color: tok('onSecondaryAction') }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: tok('onBackground') }}>
              Your response was sent
            </Typography>
            {(
              <>
                <Typography variant="body2" sx={{ color: tok('onBackgroundMuted') }}>
                  Thank you! {title ? `“${title}” has your answers.` : ''}
                </Typography>
                <Typography variant="body2" sx={{ color: tok('onBackgroundMuted') }}>
                  Have a question or anything to add? Tap Done to chat with us —
                  everything else happens right there.
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              background: tok('card'),
              color: tok('onSurface'),
              border: `1px solid ${tok('cardBorder')}`,
            }}
          >
            <Box sx={{ p: 2.25 }}>
              <Typography sx={{ mb: 0.75, fontFamily: DISPLAY_FONT, fontSize: 24, color: tok('onSurface') }}>
                {reviewing ? 'Review your answers' : 'Your answers'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.75 }}>
                {reviewing
                  ? 'Fix anything that doesn’t look right, then tap Submit below.'
                  : status === 'live'
                    ? 'They fill in as you speak.'
                    : 'Tap the mic to start talking.'}
              </Typography>

              {fields.map((field) => (
                <Box
                  key={field.name}
                  sx={{
                    p: 1.5, mt: 1.25,
                    borderRadius: '14px', background: tok('surface'),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 500, fontSize: '0.82rem', color: tok('onSurfaceMuted') }}
                    >
                      {field.name}
                      {field.optional ? ' · optional' : ''}
                    </Typography>
                    {!reviewing && draft[field.name] && (
                      <CheckCircleIcon sx={{ fontSize: 13, color: 'success.main' }} />
                    )}
                  </Box>
                  {imageFieldNames.has(field.name) ? (
                    // Image-typed field — thumbnail + Retake (spec §5.6).
                    // Answered only via request_photo, never a text edit.
                    <Box sx={{ mt: 0.5 }}>
                      {isStagedPhotoUrl(reviewing ? edits[field.name] : draft[field.name]) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            component="img"
                            src={reviewing ? edits[field.name] : draft[field.name]}
                            alt={field.name}
                            sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
                          />
                          {reviewing && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => triggerRetake(field.name)}
                              disabled={submitting || retakingField === field.name}
                              startIcon={
                                retakingField === field.name ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <PhotoCameraIcon fontSize="small" />
                                )
                              }
                              sx={{ borderRadius: 2, textTransform: 'none' }}
                            >
                              {retakingField === field.name ? 'Uploading…' : 'Retake'}
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          {reviewing ? 'No photo — collected by voice only.' : '—'}
                        </Typography>
                      )}
                      {retakeError[field.name] && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          {retakeError[field.name]}
                        </Typography>
                      )}
                      {reviewing && !field.optional && !isStagedPhotoUrl(edits[field.name]) && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          Photo required
                        </Typography>
                      )}
                    </Box>
                  ) : reviewing ? (
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={6}
                      size="small"
                      inputProps={{ 'aria-label': field.name }}
                      value={edits[field.name] || ''}
                      onChange={(e) =>
                        setEdits((prev) => ({ ...prev, [field.name]: e.target.value }))
                      }
                      disabled={submitting}
                      sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{
                        wordBreak: 'break-word',
                        transition: 'color 0.3s ease',
                        color: draft[field.name] ? 'text.primary' : 'text.disabled',
                      }}
                    >
                      {draft[field.name] || '—'}
                    </Typography>
                  )}
                </Box>
              ))}

              {status === 'connecting' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={22} />
                </Box>
              )}
            </Box>
          </Card>
        )}

        {/* Link button from the agent: another form (show_form_link) navigates
            away; a business web page (show_link, external) opens in a new tab
            and the call keeps running. */}
        {linkOffer && !finished && status !== 'error' && (
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => {
              if (linkOffer.external) {
                window.open(linkOffer.url, '_blank', 'noopener');
              } else {
                window.location.href = linkOffer.url;
              }
            }}
            sx={{
              mt: 1.5,
              borderRadius: 999,
              py: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              borderWidth: 1.5,
              color: tok('onBackground'),
              borderColor: tok('onBackgroundMuted'),
              '&:hover': { borderWidth: 1.5, borderColor: tok('onBackground') },
            }}
          >
            Open “{linkOffer.title}” →
          </Button>
        )}
      </Box>

      {/* ===== Bottom bar ===== */}
      <Box
        sx={{
          p: 2,
          pt: 1.5,
          pb: 'max(16px, env(safe-area-inset-bottom))',
          borderRadius: '24px 24px 0 0',
          backgroundColor: tok('dock'),
          color: tok('onBackground'),
        }}
      >
        {finished ? (
          <Button fullWidth variant="contained" size="large" onClick={onDoneToChat} sx={primaryButtonSx}>
            Done
          </Button>
        ) : reviewing ? (
          <>
            {submitError && (
              <Typography variant="body2" color="error" sx={{ mb: 1, textAlign: 'center' }}>
                {submitError}
              </Typography>
            )}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={submitting}
              sx={primaryButtonSx}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit ✓'}
            </Button>
            <Button
              fullWidth
              color="inherit"
              onClick={dismissReview}
              disabled={submitting}
              sx={{ mt: 0.5, textTransform: 'none', color: tok('onBackground') }}
            >
              Keep talking instead
            </Button>
          </>
        ) : status === 'error' ? null : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: tok('onBackgroundMuted') }}>
              {status === 'live' ? 'Tap End to hang up' : statusLabel}
            </Typography>
            <Button
              onClick={micLive ? stop : start}
              disabled={status === 'connecting'}
              aria-label={micLive ? 'End voice call' : 'Start voice'}
              startIcon={
                status === 'connecting' ? (
                  <CircularProgress size={18} color="inherit" />
                ) : micLive ? (
                  <StopIcon />
                ) : (
                  <MicIcon />
                )
              }
              sx={{
                ...primaryButtonSx,
                px: 3,
                py: 1.25,
                minWidth: 120,
                '&.Mui-disabled': { background: tok('secondaryAction'), color: tok('onSecondaryAction') },
              }}
            >
              {micLive ? 'End' : 'Talk'}
            </Button>
          </Box>
        )}
      </Box>

      {/* ===== Swipeable photo gallery — the call keeps going underneath.
           Also opened by the agent's show_image, with its image first. ===== */}
      <PhotoGallery open={viewerOpen} urls={viewerUrls} micLive={micLive} onClose={closeViewer} />

      {/* ===== request_photo camera sheet (spec §5) — the mic is already
           muted (useVoiceSession); this opens over the call UI until the
           photo uploads or the customer cancels. ===== */}
      <input
        ref={cameraFileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleCameraFileSelected}
      />
      <input
        ref={retakeFileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleRetakeFileSelected}
      />
      <Dialog
        open={cameraRequest !== null}
        onClose={() => { /* no-op — cancel is explicit below */ }}
        PaperProps={{ sx: { borderRadius: 4, minWidth: 280 } }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
              backgroundColor: tok('surface'),
              color: tok('accent'),
            }}
          >
            {photoUploading ? <CircularProgress size={28} /> : <PhotoCameraIcon sx={{ fontSize: 30 }} />}
          </Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {photoUploading ? 'Uploading photo…' : `Photo for "${cameraRequest?.fieldName || ''}"`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your mic is paused while the camera is open.
          </Typography>
          {photoError && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {photoError}
            </Typography>
          )}
          <Button
            fullWidth
            variant="contained"
            onClick={() => cameraFileInputRef.current?.click()}
            disabled={photoUploading}
            startIcon={<PhotoCameraIcon />}
            sx={{ ...primaryButtonSx, py: 1, mb: 1 }}
          >
            {photoError ? 'Try again' : 'Choose photo'}
          </Button>
          <Button
            fullWidth
            color="inherit"
            startIcon={<CloseIcon />}
            onClick={cancelPhotoCapture}
            disabled={photoUploading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default VoicePanel;
